// features/standalone-versions/use-create-standalone-version.ts
//
// MAKING A STANDALONE VERSION, with what the platform already has.
//
// A version is created from a page's published content: the backend compiles
// the page's reader template with the version's data and points the version
// at a content pointer. For a standalone version there is no published page,
// so the pieces a publish would have left behind are made here, the way the
// editor makes them for a story, and handed to the same create-version call:
//
//   1. the caller's host page (server-side, made on first use);
//   2. the content JSON at a pointer, plus a preview page -- the same upload
//      every autosave does (useUpdatePreviewAndMeta);
//   3. the reader template, rendered from that story and uploaded under a
//      canonical of the version's own (useGenerateCanonical);
//   4. the version, told to take its template from that canonical
//      (duplicateFromCanonicalId) and its content from that pointer.
//
// The version opens in the editor as any version does, and every later save
// goes the ordinary version way.

import { track } from "@/features/analytics";
import { useAccountStore } from "@features/account-management";
import { designSystemApi, pagesApi } from "@/shared/services";
import type { IVersion } from "@/types";
import { useCreateVersionMutation } from "@api/versions";
import type { StoryType } from "@config";
import { useCallback } from "react";
import { toast } from "react-toastify";
import uuid from "react-uuid";
import { useGenerateCanonical } from "@/providers/hooks/use-generate-canonical";
import { usePublish } from "@/providers/hooks/use-publish";
import { PublishStatus } from "@/providers/rendering-provider/types";
import { useGetStoryFastVersionVars } from "@/providers/hooks/use-get-story-fast-version-vars";
import { useUpdatePreviewAndMeta } from "@/providers/hooks/use-update-preview-and-meta";
import type { TPage } from "@/shared/services";
import {
  standaloneVersionData,
  standaloneVersionStory,
  type VersionOrigin,
} from "./standalone-version";

export type CreateStandaloneVersionInput = {
  /** The base: a template, a picked document, a blank story. */
  template: StoryType;
  title: string;
  /**
   * The org story the version is made from, when it is one: the version
   * hangs off that story like any version of it. Without one -- saved
   * slides, a Storydoc template, AI from scratch, a blank page -- it hangs
   * off the caller's hidden host page and stands alone.
   */
  basePageId?: string | null;
  /** Where it came from; the Versions page says so. */
  origin: VersionOrigin;
  /**
   * Keep the template's own theme. Off, the account's default theme is
   * stamped on, as a new story gets it.
   */
  keepTemplateDesignSystem?: boolean;
  skipToast?: boolean;
};

export type CreatedStandaloneVersion = {
  /** The page the version hangs off: the base story, or the host. */
  page: TPage;
  version: IVersion;
  /** Whether it hangs off the host rather than a story of the org's. */
  standalone: boolean;
};

/**
 * The base story, when it can carry a version: it has to be readable and to
 * have a canonical, which the reader URL is built from. A Draft is published
 * first -- the Versions page lists the versions of Live stories only, and a
 * version of a Draft would be nowhere to be found. Null when none of that
 * can be had, and the host takes the version.
 *
 * PUBLISHING SOMEBODY'S DRAFT IS NOT A DETAIL. It changes what that document
 * is: a draft nobody could open becomes a live one with a reader URL, and the
 * user asked to make a version, not to publish. It still happens -- a version
 * of a draft is a version nobody can find -- but it is said out loud, so a
 * user who did not want it knows to set the story back to Draft.
 */
const readBasePage = async (
  basePageId: string | null | undefined,
  publish: (story: StoryType) => Promise<{ status: string; page?: StoryType }>,
): Promise<TPage | null> => {
  if (!basePageId) return null;
  try {
    const page = await pagesApi.getPageById(basePageId);
    if (!page?.settings?.canonical) return null;
    if (page.status === "Live") return page;
    const rawTitle = page.settings?.title;
    const title = typeof rawTitle === "string" ? rawTitle.trim() : "";
    toast.info(
      title
        ? `Publishing "${title}" so the version has a link of its own.`
        : "Publishing the document so the version has a link of its own.",
    );
    const published = await publish(page as unknown as StoryType);
    if (published.status !== PublishStatus.Success) {
      console.error(
        `The base story could not be published (${published.status}); the version stands alone.`,
      );
      return null;
    }
    // Re-read rather than trust the publish result: it comes back without
    // the settings the story JSON is filed under.
    const live = await pagesApi.getPageById(basePageId);
    return live?.status === "Live" ? live : null;
  } catch (error) {
    console.error(
      "Failed to read the base story; the version stands alone:",
      error,
    );
    return null;
  }
};

export const useCreateStandaloneVersion = () => {
  const account = useAccountStore((state) => state.account);
  const updatePreviewAndMeta = useUpdatePreviewAndMeta();
  const { generateCanonical } = useGenerateCanonical();
  const handleVariables = useGetStoryFastVersionVars();
  const { handlePublish } = usePublish();
  const [createVersion] = useCreateVersionMutation();

  return useCallback(
    async ({
      template,
      title,
      origin,
      basePageId,
      keepTemplateDesignSystem = false,
      skipToast = false,
    }: CreateStandaloneVersionInput): Promise<CreatedStandaloneVersion> => {
      track("click create standalone version");
      try {
        const basePage = await readBasePage(basePageId, (story) =>
          handlePublish({ force: true, story }),
        );
        const page = basePage ?? (await pagesApi.getOrCreateHostPage());
        const standalone = !basePage;

        let designSystem: Record<string, string> | undefined;
        let customColors: string[] | undefined;
        if (!keepTemplateDesignSystem && account?.dsId) {
          const theme = await designSystemApi.getThemeById(account.dsId);
          designSystem = theme.designSystem as Record<string, string>;
          customColors = theme.customColors;
        }

        const story = standaloneVersionStory({
          template,
          page,
          title,
          designSystem,
          customColors,
          account,
        });

        // THE VARIABLES THE CONTENT USES. A story learns its list when it
        // is published; the version's page never is, so the list is read
        // now, from the content, and kept on the version for the share
        // dialog's "Fill out your version variables" to offer.
        const fsVars = handleVariables({ story: story as never });
        (story as { settings: Record<string, unknown> }).settings.fs = {
          ...((story as { settings: { fs?: Record<string, unknown> } }).settings
            .fs ?? {}),
          "fs-vars": fsVars,
        };

        // The content at a pointer and a preview page, as an autosave makes
        // them. skipUpdate: the host page itself is not to be touched.
        const produced = await updatePreviewAndMeta({
          story: story as never,
          skipUpdate: true,
        });
        const contentPointer = produced?.settings?.contentPointer;
        if (!produced || typeof contentPointer !== "string") {
          throw new Error("Failed to upload the version's content");
        }

        // The reader template, under a canonical of the version's own.
        const versionCanonical = uuid();
        await generateCanonical(story as never, versionCanonical);

        const version = await createVersion({
          id: page._id,
          data: standaloneVersionData(title, origin, { fsVars, standalone }),
          isPublic: true,
          expireAt: null,
          contentPointer,
          duplicateFromCanonicalId: versionCanonical,
          previewUrl: produced.previewUrl ?? null,
        }).unwrap();

        if (!skipToast) toast.success(`"${title}" created`);
        track("success create standalone version", {
          "version id": version._id,
          "page id": page._id,
          standalone,
        });
        return { page, version, standalone };
      } catch (error) {
        console.error("Failed to create the version:", error);
        track("failed create standalone version", { error });
        toast.error("Failed to create version");
        throw error;
      }
    },
    [
      account,
      updatePreviewAndMeta,
      generateCanonical,
      handleVariables,
      handlePublish,
      createVersion,
    ],
  );
};
